import { useState } from 'react'
import { ApiError } from '@/api/client'
import { teams as api } from '@/api/endpoints'
import type { Team, TeamRole } from '@/api/types'
import { Err } from '@/components/Err'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { teamRoleWord } from '@/domain/words'
import { usePicked } from '@/hooks/usePicked'
import { useSession } from '@/hooks/useSession'
import { useWorldRefresh } from '@/hooks/useWorld'
import { DASH } from '@/ui/format'
import { LIST, PeoplePicker } from './user/PeoplePicker'
import type { Candidate } from './user/teamPick'

// 팀의 멤버 창(F-83) — 팀 화면의 `멤버 수정` 이 연다. 대화 상자의 본문만이고 `<Dialog>` 는 `TeamList` 가 갖는다.
// 왼쪽 칸은 `직위`·`권한` 창과 같은 `PeoplePicker` 이고 지금 멤버가 골라진 채 열린다 — 고른 목록이 곧 원하는 멤버다.
// **멤버의 조작은 전부 이 창이다**(F-87) — 팀 화면의 멤버 줄에는 버튼이 없다. 고르기를 풀면 제거이고,
// 골라진 사람마다 `관리자` 체크가 그 사람의 역할이다(고른 사람만 보이는 목록에 선다). 새로 넣는 사람은 멤버로 시작한다.
// 오른쪽 칸은 적용할 차이를 사람 이름으로 보인다. 빠질 사람과 역할이 바뀔 사람을 기억에 맡기지 않는다.
//
// **적용은 차이만 보낸다.** 새로 고른 사람과 역할을 바꾼 사람에게만 그 역할로 `PUT` 한다 — `PUT` 은 역할을 덮어쓰는
// upsert 라 이미 든 관리자에게 `member` 를 보내면 강등이다(`teamPick.ts` 의 `pairs` 와 같은 이유). 그래서 넣기는
// 기준(연 순간의 멤버)에 없는 것만으로는 모자라고 **지금 팀에도 없어야 넣는다** — 창을 연 사이 다른 곳에서 들어와
// 관리자가 된 사람이 있다. 그런 사람의 기준 역할은 지금 팀의 것이다.
// 고른 사람은 이 창이 사람째로 쥔다(`hooks/usePicked.ts`) — 찾는 말을 바꿔 목록에서 사라진 사람도 넣을 사람과
// 이름으로 남고, 고른 목록에서 풀 수 있다. 처음 값은 부르는 쪽이 준 지금 멤버의 줄이다.
// **관리자가 되는 쓰기가 먼저다** — 내리기와 빼기는 서버가 남은 관리자를 세므로(`keepsAnOwner`) 새 관리자가 먼저 서야
// 옛 관리자를 내리거나 뺄 수 있다. 된 만큼 기준이 따라 움직여 실패한 사람만 차이로 남고, 같은 버튼이 곧 재시도다.
// **서버가 거절할 것은 창이 먼저 안다** — 고른 사람 중 관리자가 하나만 남으면 그 사람은 고르기를 풀 수도 멤버로 내릴
// 수도 없다. 최고 관리자가 아닌 사람은 자기 줄의 고르기도 역할도 잠긴다: 그에게 이 팀을 관리하는 자리는 이 팀의
// 관리자 역할뿐이라 자기를 빼거나 내리면 그 자리를 잃는다. 최고 관리자는 든 팀이든 아니든 모든 팀을 관리하므로
// 자기 줄도 남의 줄과 같다 — 잠그면 서버가 받아 줄 쓰기를 화면이 지어낸 이유로 막는다.
// `관리자` 체크를 풀면 기준의 역할로 돌아간다 — 관리자가 아니던 사람은 그 역할(`보기만` 도)이고, 관리자이던 사람과
// 넣을 사람만 멤버다. 켰다 끈 체크가 보기만인 사람을 멤버로 올리는 차이가 되면 안 된다.
//
// **후보는 탈퇴하지 않은 모든 사람에서 서버가 찾고 두 관리자에게 같다**(F-91) — 관리자가 팀에 넣을 사람의 아이디를 알 길이
// 이 목록뿐이라, 관리자의 사용자 목록도 모든 사람을 담는다. 그래서 아이디를 적어 한 사람씩 찾는 칸이 없다.

export function TeamMembers({
  team,
  seed,
  teams,
  onClose,
  onDone,
}: {
  team: Team
  /** 지금 멤버의 줄 — 골라진 채 연다. */
  seed: Candidate[]
  /** 찾은 사람의 소속을 잇는 팀 목록(`World` 의 것). */
  teams: Team[]
  onClose: () => void
  onDone: () => void
}) {
  const me = useSession()
  const refreshWorld = useWorldRefresh()
  // 연 순간의 멤버가 기준이다. 폴링이 팀을 갈아도 고르던 것이 흔들리지 않는다.
  const [now, setNow] = useState<Map<string, TeamRole>>(() => new Map(team.members.map((m) => [m.user_id, m.role])))
  // 이 창에서 바꾼 역할. 바꾸지 않은 사람은 기준의 역할이다.
  const [roles, setRoles] = useState<Record<string, TeamRole>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [dropped, setDropped] = useState<string[]>([])

  // 지금 팀 — 폴링이 갈아 준다. 줄의 자리 표시와 넣기의 대상은 이것을 본다.
  const live = new Map(team.members.map((m) => [m.user_id, m.role]))
  // 기준의 역할 — 연 순간의 멤버이거나, 창을 연 사이 다른 곳에서 들어온 사람의 지금 역할이다. 없으면 넣을 사람이다.
  const base = (id: string) => now.get(id) ?? live.get(id)
  // 원하는 역할 — 이 창에서 바꾼 것이 먼저이고, 넣을 사람은 멤버로 시작한다.
  const roleOf = (id: string): TeamRole => roles[id] ?? base(id) ?? 'member'
  const ownersIn = (chosen: string[]) => chosen.filter((x) => roleOf(x) === 'owner')
  const self = (id: string) => id === me.id && now.has(id) && me.role !== 'superadmin'
  // 고르기를 풀 수 없는 사람. 고른 목록을 인자로 받는다 — 끌기가 관리자 둘을 한 번에 지나가도 마지막 하나는 남는다.
  const fixed = (id: string, chosen: string[]) =>
    self(id) || (roleOf(id) === 'owner' && chosen.includes(id) && ownersIn(chosen).length <= 1)
  const who = usePicked(seed, fixed)
  const want = who.ids
  // 역할을 바꿀 수 없는 사람 — 같은 규칙이다. 고른 관리자가 하나뿐이면 그 사람은 멤버가 될 수 없다.
  const roleFixed = (id: string) => self(id) || (roleOf(id) === 'owner' && ownersIn(want).length <= 1)
  // 체크는 관리자인가 아닌가만 정한다. 풀면 기준의 역할이고, 기준과 같아진 역할은 차이에서 빠진다.
  const setOwner = (id: string, owner: boolean) => {
    if (roleFixed(id)) return
    const was = base(id)
    const start: TeamRole = was ?? 'member'
    const role: TeamRole = owner ? 'owner' : start === 'owner' ? 'member' : start
    setRoles((r) => {
      const next = { ...r }
      if (role === start) delete next[id]
      else next[id] = role
      return next
    })
  }

  const add = want.filter((id) => base(id) === undefined)
  const change = want.filter((id) => {
    const was = base(id)
    return was !== undefined && roleOf(id) !== was
  })
  const remove = [...now.keys()].filter((id) => !want.includes(id))
  const nameOf = (id: string) => who.known(id)?.user.username ?? team.members.find((m) => m.user_id === id)?.username ?? DASH
  const roleWord = (id: string) => teamRoleWord(roleOf(id))

  const apply = async () => {
    setBusy(true)
    setError(null)
    setDropped([])
    let first: unknown = null
    const next = new Map(now)
    const gone: string[] = []
    const puts = [...add, ...change]
    const order = [...puts.filter((id) => roleOf(id) === 'owner'), ...puts.filter((id) => roleOf(id) !== 'owner')]
    for (const id of order) {
      const role = roleOf(id)
      try {
        await api.setMember(team.id, id, role)
        next.set(id, role)
      } catch (e) {
        // 탈퇴했거나 비활성화된 계정(409 `user_inactive`)은 넣을 수도 역할을 바꿀 수도 없다 — 고른 칸과 기준에서 빼고 한 줄로 말한다.
        // 고르는 자리의 찾기에 없는 사람이라 남겨 두면 풀 길 없이 적용마다 같은 거절이고, 기준에 남기면 다음 적용이 빼기를 보낸다.
        if (e instanceof ApiError && e.reason === 'user_inactive') {
          next.delete(id)
          gone.push(id)
        } else if (first === null) first = e
      }
    }
    for (const id of remove) {
      try {
        await api.removeMember(team.id, id)
        next.delete(id)
      } catch (e) {
        // 그 사람이 이미 팀에 없다는 404(`user_not_found`)만 원하던 상태라 성공으로 센다. 팀이 보이지 않는 404 —
        // 창을 연 사이 내가 그 팀에서 빠졌거나 팀이 지워졌다 — 는 아무도 빼지 못한 것이라 오류다.
        if (e instanceof ApiError && e.status === 404 && e.reason === 'user_not_found') next.delete(id)
        else if (first === null) first = e
      }
    }
    setBusy(false)
    if (first === null && gone.length === 0) {
      onDone()
      return
    }
    if (gone.length > 0) who.only(new Set(want.filter((id) => !gone.includes(id))))
    setNow(next)
    setError(first)
    setDropped(gone.map(nameOf))
    refreshWorld()
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{team.display_name} 멤버</DialogTitle>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
        <PeoplePicker
          id="team-members-users"
          teams={teams}
          chosen={who.chosen}
          onMember={who.set}
          onFound={who.renew}
          aside={(p) => {
            const role = live.get(p.user.id)
            return role === undefined ? undefined : teamRoleWord(role)
          }}
          locked={(p) => fixed(p.user.id, want)}
          control={(p) => (
            <label className="flex shrink-0 items-center gap-1.5 text-[12px] text-muted-foreground">
              <Checkbox
                aria-label={`${p.user.username} ${teamRoleWord('owner')}`}
                checked={roleOf(p.user.id) === 'owner'}
                disabled={busy || roleFixed(p.user.id)}
                onCheckedChange={(on) => setOwner(p.user.id, on === true)}
              />
              {teamRoleWord('owner')}
            </label>
          )}
        />

        <div className="grid content-start gap-3">
          <Names id="team-members-add" label="추가" ids={add} nameOf={nameOf} word={roleWord} />
          <Names id="team-members-role" label="역할 변경" ids={change} nameOf={nameOf} word={roleWord} />
          <Names id="team-members-remove" label="제거" ids={remove} nameOf={nameOf} />
          <div className="flex justify-end">
            <Button type="button" size="sm" disabled={busy || add.length + change.length + remove.length === 0} onClick={() => void apply()}>
              {busy ? '적용 중' : '적용'}
            </Button>
          </div>
        </div>
      </div>

      {dropped.length > 0 ? (
        <p role="status" className="text-[13px] text-warn-ink">{`탈퇴했거나 비활성화된 ${dropped.join(', ')} 의 고르기를 풀었습니다.`}</p>
      ) : null}
      <Err error={error} />
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
          닫기
        </Button>
      </DialogFooter>
    </>
  )
}

/**
 * 적용할 차이 한 쪽 — 이름과, 역할이 정해지는 쪽이면 그 역할이 선다. 목록 모양은 사람 목록 칸의 것이고
 * 높이만 내용을 따른다(`권한` 창의 확인 목록과 같다).
 */
function Names({
  id,
  label,
  ids,
  nameOf,
  word,
}: {
  id: string
  label: string
  ids: string[]
  nameOf: (id: string) => string
  word?: (id: string) => string
}) {
  return (
    <div className="grid gap-1.5">
      <Label id={id}>{label}</Label>
      <ul aria-labelledby={id} className={`${LIST} h-auto max-h-48 max-sm:h-auto`}>
        {ids.length === 0 ? (
          <li className="px-3 py-2 text-[12px] text-muted-foreground">사용자 없음</li>
        ) : (
          ids.map((uid) => (
            <li key={uid} className="flex items-center gap-2 px-3 py-1.5 text-[13px]">
              <span className="truncate font-medium">{nameOf(uid)}</span>
              {word ? <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{word(uid)}</span> : null}
            </li>
          ))
        )}
      </ul>
    </div>
  )
}
